import React from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import injectSheet from "react-jss";
import PropTypes from "prop-types";

import { IconClose } from "folksam-ui";

const styles = ({ theme, legacyTheme }) => ({
  hide: {
    visibility: "hidden"
  },
  show: {
    visibility: "visible"
  },
  arrow: {
    top: props => {
      return props.offsetTop - 20;
    },
    /** Center arrow over the tooltip component */
    left: props => {
      const arrowOffset = 8;

      return props.tooltipRect.left
        ? props.tooltipRect.left + (props.tooltipRect.width / 2 - arrowOffset)
        : null;
    },
    zIndex: 10,
    border: "8px solid transparent",
    borderTopColor: theme.colors.quanternary5,
    boxSizing: "border-box",
    content: "''",
    position: "absolute",
    width: 0,
    height: 0
  },
  arrowCollapsed: {
    display: "none"
  },
  arrowShadow: {
    top: props => {
      return props.offsetTop - 34;
    },
    left: props => {
      const arrowOffset = 9;

      return props.tooltipRect.left
        ? props.tooltipRect.left + (props.tooltipRect.width / 2 - arrowOffset)
        : null;
    },
    borderColor: "rgba(0, 0, 0, 0.15)",
    transform: "rotate(45deg)",
    border: "9px solid transparent",
    boxSizing: "border-box",
    boxShadow: "0px 1px 5px 1px rgba(0,0,0,0.10)",
    content: "''",
    position: "absolute",
    width: 0,
    height: 0
  },
  arrowShadowCollapsed: {
    display: "none"
  },
  message: {
    ...legacyTheme.font.primary,
    height: "auto",
    position: "absolute",
    top: props => {
      return props.offsetTop - (props.messageRect.height / 2 + 20);
    },
    // top: props => {
    //   return props.offsetTop - props.messageRect.height - 60;
    // },
    padding: "20px",
    paddingRight: props => (props.toggleOnHover ? "20px" : "50px"),
    background: theme.colors.white,
    width: props => {
      const tabletUpBreakpoint = 600;

      return props.bodyWidth >= tabletUpBreakpoint ? 350 : "calc(100% - 38px)";
    },
    // borderRadius: "5px",
    fontFamily: theme.fonts.sans,
    fontSize: "0.875rem",
    lineHeight: 1.25,
    border: "solid 1px rgba(0, 0, 0, 0.15)",
    boxShadow: "0px 1px 5px 1px rgba(0, 0, 0, 0.15)",
    boxSizing: "border-box",
    color: "#363636",
    zIndex: 1,
    cursor: "pointer",
    left: props => {
      const horizontalPadding = props.toggleOnHover ? 40 : 70;
      const arrowOffset = 9;
      const arrowPosition =
        props.tooltipRect.left + (props.tooltipRect.width / 2 - arrowOffset);
      const messageWillFitLeft =
        props.tooltipRect.left + props.tooltipRect.width / 2 >
        props.messageRect.width + horizontalPadding;
      const tooltipCenterPositionFromRight =
        props.bodyWidth -
        (props.tooltipRect.left + props.tooltipRect.width / 2);
      const totalMsgWidth = props.messageRect.width + horizontalPadding;
      const messageWillFitRight =
        tooltipCenterPositionFromRight > totalMsgWidth;
      const direction = props.direction;

      if (!messageWillFitRight) {
        // If won't fit right, don't mind the left positioning
        return null;
      }

      switch (direction) {
        // Align left
        case "left":
          return messageWillFitLeft
            ? arrowPosition - props.messageRect.width
            : 19;
        //Align right
        case "right":
          return messageWillFitLeft ? arrowPosition + 20 : 19;
        // return messageWillFitLeft ? arrowPosition - 20 : 19;
        // Align center
        default:
          return messageWillFitLeft
            ? arrowPosition - (props.messageRect.width + 20) / 2
            : 19;
      }
    },
    right: props => {
      // Only used if won't fit on right edge
      const horizontalPadding = props.toggleOnHover ? 40 : 70;
      const tooltipCenterPositionFromRight =
        props.bodyWidth -
        (props.tooltipRect.left + props.tooltipRect.width / 2);
      const totalMsgWidth = props.messageRect.width + horizontalPadding;
      const messageWillFitRight =
        tooltipCenterPositionFromRight > totalMsgWidth;

      if (messageWillFitRight) {
        return null;
      }
      return 19;
    }
  },
  messageCollapsed: {
    height: 0,
    overflow: "hidden",
    border: 0
  },
  close: {
    borderRadius: "50%",
    backgroundColor: theme.colors.primary2,
    color: theme.colors.white,
    width: "28px",
    height: "28px",
    lineHeight: "28px",
    border: "none",
    position: "absolute",
    top: "12px",
    right: "12px",
    boxSizing: "border-box",
    padding: "0px",
    display: "flex",
    justifyContent: "center"
  },

  icon: {
    width: "12px",
    fill: theme.colors.white
  }
});

class TooltipMessage extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement("div");
    this.body = document.getElementsByTagName("body")[0];
  }

  static propTypes = {
    tooltipRect: PropTypes.object,
    messageRect: PropTypes.object,
    offsetTop: PropTypes.number,
    bodyWidth: PropTypes.number,
    isOpen: PropTypes.bool,
    toggleOnHover: PropTypes.bool,
    direction: PropTypes.string
  };

  componentDidMount() {
    this.body.appendChild(this.el);
  }

  componentWillUnmount() {
    this.body.removeChild(this.el);
  }

  render() {
    const { classes, isOpen, children, toggleOnHover } = this.props;

    return ReactDOM.createPortal(
      <div
        role="tooltip"
        className={classNames(classes.hide, { [classes.show]: isOpen })}
      >
        {/* <span
          className={classNames(classes.arrowShadow, {
            [classes.arrowShadowCollapsed]: !isOpen
          })}
        /> */}
        <div
          className={classNames(classes.message, {
            [classes.messageCollapsed]: !isOpen
          })}
          tabIndex={-1}
        >
          {children}
          {toggleOnHover ? null : (
            <div className={classes.close}>
              <IconClose className={classes.icon} />
            </div>
          )}
        </div>
        {/* <span
          className={classNames(classes.arrow, {
            [classes.arrowCollapsed]: !isOpen
          })}
        /> */}
      </div>,
      this.el
    );
  }
}

export default injectSheet(styles, { inject: ["classes"] })(TooltipMessage);
